import React from 'react';
import { Button, Message, OptionalSection } from '@weezevent/nacre';
import Waiting from '../Waiting';
import BillingForm from './BillingForm';
import ValidationModal from '../ValidationModal';
import { useTranslation } from 'react-i18next';
import { useSegment } from '../../../../../components/tracking/trackingProvider';
import i18n from 'i18next';
import { FormPreview } from '../../../../forms/components/formPreview';
import css from '../../styles.module.css';
import useAppContext from '../../../../../context/useAppContext';
import { ORIGIN_STOCK_PRINT } from '../../../../../models/order';

export default function AddAttendeeBuyerStep({ form, onChangeForm, buyerFields, loadBuyerFields, handleSubmit, onConstructAttendees, isSubmitting }) {
    const locale = i18n.language;

    const [addInvoiceOpen, setAddInvoiceOpen] = React.useState(false);
    const [previousFields, setPreviousFields] = React.useState(false);
    const { t } = useTranslation();
    const { track } = useSegment();
    const [{ event }] = useAppContext();

    const onChange = React.useCallback(
        (key, value) => {
            let tmpBuyerForm = form.buyer_form;
            tmpBuyerForm[key] = value;
            onChangeForm('buyer_form', tmpBuyerForm);
        },
        [form.buyer_form, onChangeForm]
    );

    const onChangeBuyerForm = React.useCallback(
        value => {
            onChangeForm('buyer_form', value);
        },
        [onChangeForm]
    );

    const onChangeBillingForm = React.useCallback(
        (key, value) => {
            let tmpBillingForm = form.billing_form;
            tmpBillingForm[key] = value;
            onChangeForm('billing_form', tmpBillingForm);
        },
        [form, onChangeForm]
    );

    const handleModal = React.useCallback(() => {
        if (form.payment_type !== ORIGIN_STOCK_PRINT) {
            onChangeForm('validationModal', true);
        }
        const ratesTypesList = form.selected_rates.reduce((all_types, selected) => {
            const types = { fixed: 'Fixed', free: 'Free', open: 'Donation' };
            if (!all_types.includes(types[selected.rate.type])) all_types.push(types[selected.rate.type]);
            return all_types;
        }, []);
        track('ACTION > SAVE ATTENDEE', {
            'Page Title': 'ADD ATTENDEES',
            'Event type name': event.type,
            'Rate quantity': form.selected_rates.length,
            'Attendee quantity': form.attendees.length,
            'Placement Type': form.use_sessions ? 'Session' : form.use_inventories ? 'Numbered seating' : 'Classic',
            'Rate Types': ratesTypesList,
            'Payment Status': form.status === 'valid' ? 'Paid' : 'Booked'
        });
    }, [form, event, onChangeForm]);

    // we fill checkbox form fields with boolean value because default "0" & "1" values aren't saved when we don't change any field
    React.useEffect(() => {
        if (buyerFields !== null && buyerFields !== previousFields) {
            const defaultForm = {};
            buyerFields.forEach(field => {
                if (field.form_field.field_type === 'checkbox') {
                    const defaultValue =
                        field.form_field.default_value === null || field.form_field.default_value === '0'
                            ? false
                            : field.form_field.default_value === '1'
                            ? true
                            : field.form_field.default_value;
                    defaultForm[field.form_field.weez_id] = defaultValue;
                }
            });
            onChangeBuyerForm(defaultForm);
            setPreviousFields(buyerFields);
        }
    }, [buyerFields, previousFields, onChangeBuyerForm]);

    return (
        <Waiting for={!loadBuyerFields}>
            <Message type="default" title={t('attendees.addAttendees.addBuyerForm.message-title')}>
                {t('attendees.addAttendees.addBuyerForm.message-subtitle')}
            </Message>
            <FormPreview fields={buyerFields} form={form.buyer_form} onChangeForm={onChange} onChangeBuyerForm={onChangeBuyerForm} locale={locale} usage={'buyer'} />
            <OptionalSection
                open={addInvoiceOpen}
                onToggle={toggle => {
                    setAddInvoiceOpen(toggle);
                    track('ACTION > DISPLAY INVOICE INFORMATION', { 'Page Title': 'ADD ATTENDEES' });
                }}
                label={t('attendees.addAttendees.addBuyerForm.addInvoice')}
                optionalText={t('ticket.common.labelOptional')}
            >
                <BillingForm billingForm={form.billing_form} onChange={onChangeBillingForm} />
            </OptionalSection>
            <Button
                className={css['progress-buttons']}
                primary
                onClick={form.payment_type !== ORIGIN_STOCK_PRINT ? handleModal : handleSubmit}
                label={t('ticket.common.action.save')}
                disabled={isSubmitting}
            />
            <ValidationModal form={form} setForm={onChangeForm} handleSubmit={handleSubmit} isSubmitting={isSubmitting} />
        </Waiting>
    );
}
